import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorGlobal, successGlobal } from '../reducers/notifications';
import { getAuthHeader } from '../../utils/tools';
import axios from 'axios';

const buildFormData = (values, photo) => {
    const fd = new FormData();
    fd.append('name', values.name);
    fd.append('code', values.code || '');
    fd.append('gtin', values.gtin || '');
    fd.append('packagingType', values.packagingType);
    fd.append('character', values.character);
    fd.append('foodContact', values.foodContact ? 'true' : 'false');
    fd.append('recyclable', values.recyclable ? 'true' : 'false');
    fd.append('deposit', values.deposit ? 'true' : 'false');
    fd.append('usage', values.usage || '');
    fd.append('digitalPassportUrl', values.digitalPassportUrl || '');
    fd.append('description', values.description || '');
    fd.append('operationDescription', values.operationDescription || '');
    fd.append('internalNote', values.internalNote || '');
    fd.append('translations', JSON.stringify(values.translations ?? {}));
    fd.append('marketCountries', JSON.stringify(values.marketCountries ?? []));
    if (values.marketDate) fd.append('marketDate', values.marketDate instanceof Date ? values.marketDate.toISOString() : values.marketDate);
    fd.append('baseRole', values.baseRole || '');
    fd.append('producerRole', values.producerRole || 'NOT_APPLICABLE');
    if (photo) fd.append('photo', photo);
    return fd;
};

export const fetchPackagingUnits = createAsyncThunk(
    'packagingUnit/fetchPackagingUnits',
    async (_, { dispatch }) => {
        try {
            const request = await axios.get('/api/packagingunit/fetch', getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Obalové jednotky sa nepodarilo načítať.'));
            throw error;
        }
    }
);

export const addPackagingUnit = createAsyncThunk(
    'packagingUnit/addPackagingUnit',
    async ({ values, photo }, { dispatch }) => {
        try {
            const fd = buildFormData(values, photo);
            const { headers } = getAuthHeader();
            const request = await axios.post('/api/packagingunit/create', fd, { headers });
            dispatch(successGlobal('Obalová jednotka bola úspešne vytvorená.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Obalovú jednotku sa nepodarilo vytvoriť.'));
            throw error;
        }
    }
);

export const updatePackagingUnit = createAsyncThunk(
    'packagingUnit/updatePackagingUnit',
    async ({ packagingUnitId, values, photo }, { dispatch }) => {
        try {
            const fd = buildFormData(values, photo);
            fd.append('packagingUnitId', packagingUnitId);
            const { headers } = getAuthHeader();
            const request = await axios.patch('/api/packagingunit/edit', fd, { headers });
            dispatch(successGlobal('Obalová jednotka bola úspešne upravená.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Obalovú jednotku sa nepodarilo upraviť.'));
            throw error;
        }
    }
);

export const deletePackagingUnit = createAsyncThunk(
    'packagingUnit/deletePackagingUnit',
    async ({ packagingUnitId, password }, { dispatch }) => {
        try {
            const request = await axios.delete('/api/packagingunit/delete', {
                data: { packagingUnitId, password },
                ...getAuthHeader(),
            });
            dispatch(successGlobal('Obalová jednotka bola úspešne vymazaná.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Obalovú jednotku sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const addPackagingUnitComponent = createAsyncThunk(
    'packagingUnit/addPackagingUnitComponent',
    async ({ packagingUnitId, values }, { dispatch }) => {
        try {
            const request = await axios.post('/api/packagingunit/component/add', { packagingUnitId, ...values }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Komponent sa nepodarilo pridať.'));
            throw error;
        }
    }
);

export const updatePackagingUnitComponent = createAsyncThunk(
    'packagingUnit/updatePackagingUnitComponent',
    async ({ packagingUnitId, componentId, values }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/component/edit', { packagingUnitId, componentId, ...values }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Komponent sa nepodarilo upraviť.'));
            throw error;
        }
    }
);

export const deletePackagingUnitComponent = createAsyncThunk(
    'packagingUnit/deletePackagingUnitComponent',
    async ({ packagingUnitId, componentId }, { dispatch }) => {
        try {
            const request = await axios.delete('/api/packagingunit/component/delete', {
                data: { packagingUnitId, componentId },
                ...getAuthHeader(),
            });
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Komponent sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const addPackagingUnitDocument = createAsyncThunk(
    'packagingUnit/addPackagingUnitDocument',
    async ({ packagingUnitId, values }, { dispatch }) => {
        try {
            const request = await axios.post('/api/packagingunit/document/add', { packagingUnitId, ...values }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Dokument sa nepodarilo pridať.'));
            throw error;
        }
    }
);

export const deletePackagingUnitDocument = createAsyncThunk(
    'packagingUnit/deletePackagingUnitDocument',
    async ({ packagingUnitId, documentId }, { dispatch }) => {
        try {
            const request = await axios.delete('/api/packagingunit/document/delete', {
                data: { packagingUnitId, documentId },
                ...getAuthHeader(),
            });
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Dokument sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const setPackagingUnitConformityAssessment = createAsyncThunk(
    'packagingUnit/setPackagingUnitConformityAssessment',
    async ({ packagingUnitId, criterionId, status, note }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/conformity/set', { packagingUnitId, criterionId, status, note }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Vyhodnotenie sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const setPackagingUnitConformityAssessments = createAsyncThunk(
    'packagingUnit/setPackagingUnitConformityAssessments',
    async ({ packagingUnitId, entries }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/conformity/set-all', { packagingUnitId, entries }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Vyhodnotenie sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const setDistributorVerification = createAsyncThunk(
    'packagingUnit/setDistributorVerification',
    async ({ packagingUnitId, criterionId, checked }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/distributor-verification/set', { packagingUnitId, criterionId, checked }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Overenie sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const setProducerVerification = createAsyncThunk(
    'packagingUnit/setProducerVerification',
    async ({ packagingUnitId, criterionId, checked }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/producer-verification/set', { packagingUnitId, criterionId, checked }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Overenie sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const setPackagingUnitPublicPage = createAsyncThunk(
    'packagingUnit/setPackagingUnitPublicPage',
    async ({ packagingUnitId, enabled }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/public-page', { packagingUnitId, enabled }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Nastavenie verejnej stránky sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const generatePackagingUnitQrCode = createAsyncThunk(
    'packagingUnit/generatePackagingUnitQrCode',
    async ({ packagingUnitId }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/qr-code/generate', { packagingUnitId }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'QR kód sa nepodarilo vygenerovať.'));
            throw error;
        }
    }
);

export const setPackagingUnitStandardsAssessments = createAsyncThunk(
    'packagingUnit/setPackagingUnitStandardsAssessments',
    async ({ packagingUnitId, entries }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/standards-assessment/set-all', { packagingUnitId, entries }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Zoznam noriem a špecifikácií sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const setPackagingUnitDrawingDocument = createAsyncThunk(
    'packagingUnit/setPackagingUnitDrawingDocument',
    async ({ packagingUnitId, documentId, notProvided }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/drawing-document/set', { packagingUnitId, documentId, notProvided }, getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Výkresovú dokumentáciu sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const fetchConformityMeta = createAsyncThunk(
    'packagingUnit/fetchConformityMeta',
    async (_, { dispatch }) => {
        try {
            const request = await axios.get('/api/packagingunit/conformity/meta', getAuthHeader());
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Metadáta posúdenia zhody sa nepodarilo načítať.'));
            throw error;
        }
    }
);

const downloadBlob = (blobData, filename) => {
    const blob = new Blob([blobData], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

const extractBlobErrorMessage = async (error, fallback) => {
    if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
            const parsed = JSON.parse(await error.response.data.text());
            return parsed?.message ?? fallback;
        } catch {
            return fallback;
        }
    }
    return error.response?.data?.message ?? fallback;
};

export const downloadQrCodePdf = createAsyncThunk(
    'packagingUnit/downloadQrCodePdf',
    async ({ packagingUnitId, name }, { dispatch }) => {
        try {
            const response = await axios.get('/api/packagingunit/qr-code/pdf/download', {
                params: { packagingUnitId },
                responseType: 'blob',
                ...getAuthHeader(),
            });

            const safeName = (name || 'obal').replace(/[^a-zA-Z0-9-_]+/g, '_');
            downloadBlob(response.data, `QR-${safeName}.pdf`);
            return { success: true };
        } catch (error) {
            const message = await extractBlobErrorMessage(error, 'QR kód sa nepodarilo stiahnuť.');
            dispatch(errorGlobal(message));
            throw error;
        }
    }
);

export const generateTechnicalDocumentationPdf = createAsyncThunk(
    'packagingUnit/generateTechnicalDocumentationPdf',
    async ({ packagingUnitId, name, lang }, { dispatch }) => {
        try {
            const response = await axios.post(
                '/api/packagingunit/technical-documentation-pdf/generate',
                { packagingUnitId, lang },
                { responseType: 'blob', ...getAuthHeader() }
            );

            const safeName = (name || 'obalova-jednotka').replace(/[^a-zA-Z0-9-_]+/g, '_');
            downloadBlob(response.data, `Technicka-dokumentacia-${safeName}.pdf`);

            dispatch(successGlobal('Technická dokumentácia bola úspešne vygenerovaná.'));
            await dispatch(fetchPackagingUnits());
            return { success: true };
        } catch (error) {
            const message = await extractBlobErrorMessage(error, 'Technickú dokumentáciu sa nepodarilo vygenerovať.');
            dispatch(errorGlobal(message));
            throw error;
        }
    }
);

export const downloadTechnicalDocumentationGeneration = createAsyncThunk(
    'packagingUnit/downloadTechnicalDocumentationGeneration',
    async ({ packagingUnitId, generationId, name, generatedAt }, { dispatch }) => {
        try {
            const response = await axios.get('/api/packagingunit/technical-documentation-pdf/download', {
                params: { packagingUnitId, generationId },
                responseType: 'blob',
                ...getAuthHeader(),
            });

            const safeName = (name || 'obalova-jednotka').replace(/[^a-zA-Z0-9-_]+/g, '_');
            const dateSuffix = generatedAt ? new Date(generatedAt).toISOString().slice(0, 10) : '';
            downloadBlob(response.data, `Technicka-dokumentacia-${safeName}${dateSuffix ? `-${dateSuffix}` : ''}.pdf`);

            return { success: true };
        } catch (error) {
            const message = await extractBlobErrorMessage(error, 'Dokument sa nepodarilo stiahnuť.');
            dispatch(errorGlobal(message));
            throw error;
        }
    }
);

export const deleteTechnicalDocumentationGeneration = createAsyncThunk(
    'packagingUnit/deleteTechnicalDocumentationGeneration',
    async ({ packagingUnitId, generationId }, { dispatch }) => {
        try {
            const request = await axios.delete('/api/packagingunit/technical-documentation-pdf/delete', {
                data: { packagingUnitId, generationId },
                ...getAuthHeader(),
            });
            dispatch(successGlobal('Dokument bol úspešne vymazaný.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Dokument sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const generateEuDeclarationPdf = createAsyncThunk(
    'packagingUnit/generateEuDeclarationPdf',
    async ({ packagingUnitId, name, lang }, { dispatch }) => {
        try {
            const response = await axios.post(
                '/api/packagingunit/eu-declaration-pdf/generate',
                { packagingUnitId, lang },
                { responseType: 'blob', ...getAuthHeader() }
            );

            const safeName = (name || 'obalova-jednotka').replace(/[^a-zA-Z0-9-_]+/g, '_');
            downloadBlob(response.data, `EU-vyhlasenie-${safeName}.pdf`);

            dispatch(successGlobal('EÚ vyhlásenie bolo úspešne vygenerované.'));
            await dispatch(fetchPackagingUnits());
            return { success: true };
        } catch (error) {
            const message = await extractBlobErrorMessage(error, 'EÚ vyhlásenie sa nepodarilo vygenerovať.');
            dispatch(errorGlobal(message));
            throw error;
        }
    }
);

export const downloadEuDeclarationGeneration = createAsyncThunk(
    'packagingUnit/downloadEuDeclarationGeneration',
    async ({ packagingUnitId, generationId, name, generatedAt }, { dispatch }) => {
        try {
            const response = await axios.get('/api/packagingunit/eu-declaration-pdf/download', {
                params: { packagingUnitId, generationId },
                responseType: 'blob',
                ...getAuthHeader(),
            });

            const safeName = (name || 'obalova-jednotka').replace(/[^a-zA-Z0-9-_]+/g, '_');
            const dateSuffix = generatedAt ? new Date(generatedAt).toISOString().slice(0, 10) : '';
            downloadBlob(response.data, `EU-vyhlasenie-${safeName}${dateSuffix ? `-${dateSuffix}` : ''}.pdf`);

            return { success: true };
        } catch (error) {
            const message = await extractBlobErrorMessage(error, 'Dokument sa nepodarilo stiahnuť.');
            dispatch(errorGlobal(message));
            throw error;
        }
    }
);

export const confirmPackagingUnitSupplierDocs = createAsyncThunk(
    'packagingUnit/confirmSupplierDocs',
    async (packagingUnitId, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/supplier-docs/confirm', { packagingUnitId }, getAuthHeader());
            dispatch(successGlobal('Dokumenty boli potvrdené.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Potvrdenie sa nepodarilo uložiť.'));
            throw error;
        }
    }
);

export const setPackagingUnitArchived = createAsyncThunk(
    'packagingUnit/setPackagingUnitArchived',
    async ({ packagingUnitId, archived }, { dispatch }) => {
        try {
            const request = await axios.patch('/api/packagingunit/archive', { packagingUnitId, archived }, getAuthHeader());
            dispatch(successGlobal(archived ? 'Obalová jednotka bola archivovaná.' : 'Obalová jednotka bola obnovená z archívu.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Operácia sa nepodarila.'));
            throw error;
        }
    }
);

export const deleteEuDeclarationGeneration = createAsyncThunk(
    'packagingUnit/deleteEuDeclarationGeneration',
    async ({ packagingUnitId, generationId }, { dispatch }) => {
        try {
            const request = await axios.delete('/api/packagingunit/eu-declaration-pdf/delete', {
                data: { packagingUnitId, generationId },
                ...getAuthHeader(),
            });
            dispatch(successGlobal('Dokument bol úspešne vymazaný.'));
            return request.data;
        } catch (error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Dokument sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const downloadDocumentationZip = ({ packagingUnitId, name, onProgress, onTransferStart }) => async (dispatch) => {
    try {
        let transferStarted = false;
        const response = await axios.get('/api/packagingunit/documentation-zip/download', {
            params: { packagingUnitId },
            responseType: 'blob',
            onDownloadProgress: (e) => {
                if (!transferStarted) { transferStarted = true; onTransferStart?.(); }
                if (e.total) onProgress?.(Math.round((e.loaded / e.total) * 100));
            },
            ...getAuthHeader(),
        });

        onProgress?.(100);
        const safeName = (name || 'obalova-jednotka').replace(/[^a-zA-Z0-9-_]+/g, '_');
        downloadBlob(response.data, `Dokumentacia-${safeName}.zip`);
    } catch (error) {
        const message = await extractBlobErrorMessage(error, 'Dokumentáciu sa nepodarilo stiahnuť.');
        dispatch(errorGlobal(message));
        throw error;
    }
};
